// src/lib/onchain.ts
import type { InterfaceAbi } from "ethers";
import {
  BrowserProvider, Contract, JsonRpcSigner
} from "ethers";
import { type Abi, type Address, encodeFunctionData } from "viem";
import ERC20_ABI_JSON from "../abi/TestUSDC.json";
import ESCROW_ABI_JSON from "../abi/TPCParlayEscrow.json";
import { magic } from "../lib/magic";
import { defaultLogger } from "../utils/logger";
import { createSmartAccount, getSmartAccountAddress, isGasSponsorshipAvailable } from "./zerodev";

const ERC20_ABI = ERC20_ABI_JSON as InterfaceAbi;
const ESCROW_ABI = ESCROW_ABI_JSON as InterfaceAbi;
const ERC20_ABI_VIEM = ERC20_ABI_JSON as Abi;
const ESCROW_ABI_VIEM = ESCROW_ABI_JSON as Abi;

const USDC_ADDRESS   = process.env.REACT_APP_USDC_ADDRESS!;
const ESCROW_ADDRESS = process.env.REACT_APP_ESCROW_ADDRESS!;
const CHAIN_ID_DEC   = Number(process.env.REACT_APP_PUBLIC_CHAIN_ID_DEC || 84532); // Base Sepolia
// const CHAIN_ID_HEX   = "0x" + CHAIN_ID_DEC.toString(16); // "0x14A34"

const asHex = (v: string) => (v.startsWith("0x") ? v : `0x${v}`);

// Helper to get the correct network name for jiffyscan URLs
const getJiffyscanNetwork = () => CHAIN_ID_DEC === 8453 ? 'base' : 'base-sepolia';
const getJiffyscanUrl = (userOpHash: string) => 
  `https://jiffyscan.xyz/userOpHash/${userOpHash}?network=${getJiffyscanNetwork()}`;
const assertHexLen = (hex: string, bytes: number, label: string) => {
  const h = hex.startsWith("0x") ? hex.slice(2) : hex;
  if (h.length !== bytes * 2) throw new Error(`${label} must be ${bytes} bytes`);
};

async function getProvider(): Promise<BrowserProvider> {
  return new BrowserProvider(magic.rpcProvider as any);
}

async function getSignerEnsuringChain(): Promise<{ provider: BrowserProvider; signer: JsonRpcSigner }> {
  const provider = await getProvider();
  // Try EIP-1193 switch maybe
  // @ts-ignore
  const eip = (provider as any).provider || magic.rpcProvider;
  const net = await provider.getNetwork();
  if (Number(net.chainId) !== CHAIN_ID_DEC) {
    await eip.request?.({ method: "wallet_switchEthereumChain", params: [{ chainId: "0x" + CHAIN_ID_DEC.toString(16) }] });
  }
  return { provider, signer: await provider.getSigner() };
}

/**
 * Get smart account client for gas-sponsored transactions
 */
async function getSmartAccountClient(): Promise<{ 
  kernelClient: any | null; 
  publicClient: any | null;
  smartAccountAddress: Address | null;
  useFallback: boolean;
}> {
  defaultLogger.info('🔧 getSmartAccountClient: Starting...');
  
  if (!isGasSponsorshipAvailable()) {
    defaultLogger.warn('⚠️ Gas sponsorship not available, will use fallback');
    return { kernelClient: null, publicClient: null, smartAccountAddress: null, useFallback: true };
  }

  defaultLogger.info('✅ Gas sponsorship is available, attempting to create smart account...');
  
  try {
    const smartAccount = await createSmartAccount();
    defaultLogger.info('✅ Smart account client created successfully', { address: smartAccount.address });
    
    defaultLogger.info('📊 Smart Account Details', {
      smartAccountAddress: smartAccount.address,
      willUseKernelClient: true,
      note: 'Using ZeroDev Kernel client to send UserOperations'
    });
    
    return { 
      kernelClient: smartAccount.kernelClient,
      publicClient: smartAccount.publicClient,
      smartAccountAddress: smartAccount.address,
      useFallback: false
    };
  } catch (error) {
    defaultLogger.error('❌ Failed to create smart account, will use fallback', error);
    return { kernelClient: null, publicClient: null, smartAccountAddress: null, useFallback: true };
  }
}

/** Read the escrow’s configured USDC token address on-chain. */
async function readEscrowUsdc(signer: JsonRpcSigner, escrowAddr: string): Promise<string> {
  const escrow = new Contract(escrowAddr, ESCROW_ABI, signer);
  const usdcAddr: string = await escrow.usdc();
  return usdcAddr;
}

/** Ensure allowance(owner -> spender) >= required (base units). Zero-reset if needed. */
async function ensureAllowanceBaseUnits(
  signer: JsonRpcSigner,
  tokenAddr: string,
  owner: string,
  spender: string,
  required: bigint
) {
  const token = new Contract(tokenAddr, ERC20_ABI, signer);
  const current: bigint = await token.allowance(owner, spender);

  console.debug("[allowance] token:", tokenAddr, "owner:", owner, "spender:", spender, "current:", current.toString(), "need:", required.toString());

  if (current >= required) return;

  if (current > 0) {
    const tx0 = await token.approve(spender, 0);
    await tx0.wait();
  }
  const tx = await token.approve(spender, required);
  await tx.wait();
  //if transaction did not go through
  if(tx.hash === undefined) {
    throw new Error("Approval transaction failed");
  }
  return;
}

/** Place parlay using raw /quote response from backend. */
export async function placeParlayOnchain(quoteResp: any) {
  const q = quoteResp?.quote_struct;
  const sig = quoteResp?.houseSig;
  if (!q || !sig) throw new Error("Missing quote_struct or houseSig");

  // Check if we need to update wallet address for gas sponsorship
  try {
    const { isGasSponsorshipAvailable, getSmartAccountAddress } = await import('./zerodev');
    if (isGasSponsorshipAvailable()) {
      console.log('🔄 Gas sponsorship available - checking wallet address');
      const smartAccountAddress = await getSmartAccountAddress();
      if (smartAccountAddress && smartAccountAddress.toLowerCase() !== q.user.toLowerCase()) {
        console.error('❌ CRITICAL: Smart account address mismatch!');
        console.error('Smart Account:', smartAccountAddress);
        console.error('Quote User:', q.user);
        console.error('This will cause "not user" error in smart contract');
        throw new Error(`Smart account address ${smartAccountAddress} does not match quote user ${q.user}. Please refresh the page and try again.`);
      }
    }
  } catch (error) {
    console.log('Gas sponsorship check failed:', error);
  }

  const quoteStruct = {
    quoteId: asHex(q.quoteId),
    user: q.user,
    stake: q.stake,                   // string of base units
    expectedPayout: q.expectedPayout, // string of base units
    expiry: q.expiry,
    chainId: q.chainId,
    legsHash: asHex(q.legsHash),
  };
  
  // Log quote expiry details for debugging on-chain submission
  const currentTimestamp = Math.floor(Date.now() / 1000);
  defaultLogger.info("On-chain quote expiry check", {
    quote_expiry: q.expiry,
    current_timestamp: currentTimestamp,
    time_until_expiry: q.expiry - currentTimestamp,
    expired: currentTimestamp >= q.expiry
  });
  assertHexLen(quoteStruct.quoteId, 32, "quoteId");
  assertHexLen(quoteStruct.legsHash, 32, "legsHash");
  const sigHex = asHex(sig);
  assertHexLen(sigHex, 65, "houseSig");

  if (Number(q.chainId) !== CHAIN_ID_DEC) throw new Error(`Quote chainId ${q.chainId} != expected ${CHAIN_ID_DEC}`);

  // Get smart account client or fallback to regular signer
  defaultLogger.info('🔐 Getting client for transaction...');
  const { kernelClient, publicClient, smartAccountAddress, useFallback } = await getSmartAccountClient();
  
  let effectiveUser: string;
  let signer: JsonRpcSigner | null = null;
  
  if (useFallback || !kernelClient) {
    defaultLogger.info('📱 Using fallback Magic wallet');
    const { provider, signer: magicSigner } = await getSignerEnsuringChain();
    signer = magicSigner;
    effectiveUser = await magicSigner.getAddress();
    defaultLogger.info('📋 Magic wallet address', { effectiveUser });
  } else {
    defaultLogger.info('✅ Using ZeroDev Kernel client');
    effectiveUser = smartAccountAddress as string;
    defaultLogger.info('📋 Smart account address', { effectiveUser });
  }
  
  defaultLogger.info('🎯 Effective user for transaction', { effectiveUser });
  defaultLogger.info('📄 Quote user address', { quoteUser: q.user });
  
  if (effectiveUser.toLowerCase() !== String(q.user).toLowerCase()) {
    defaultLogger.error('❌ CRITICAL: Address Mismatch!', {
      effectiveUser,
      quoteUser: q.user,
      smartAccountAddress,
      usingKernelClient: !useFallback,
      gasSponsored: isGasSponsorshipAvailable(),
      match: false
    });
    defaultLogger.error('🚨 This will cause "not user" error in smart contract!', { effectiveUser, quoteUser: q.user });
    throw new Error(`Active wallet ${effectiveUser} != quote.user ${q.user}`);
  }
  
  defaultLogger.info('✅ Address match confirmed', {
    effectiveUser,
    quoteUser: q.user,
    usingKernelClient: !useFallback,
    match: true
  });

  const escrowAddr: string = quoteResp?.escrow?.address || ESCROW_ADDRESS;
  if (!escrowAddr) throw new Error("No escrow address provided");

  // Read USDC address from escrow
  let usdcAddr: string;
  if (useFallback && signer) {
    usdcAddr = await readEscrowUsdc(signer, escrowAddr);
  } else {
    // For Kernel client, just use the environment variable
    usdcAddr = process.env.REACT_APP_USDC_ADDRESS || '';
    if (!usdcAddr) {
      throw new Error('REACT_APP_USDC_ADDRESS not configured');
    }
  }
  
  defaultLogger.info("🔗 Escrow/Account Info", {
    usdcAddr,
    escrowAddr,
    smartAccountAddress: smartAccountAddress || 'N/A',
    gasSponsorship: isGasSponsorshipAvailable() ? 'ENABLED' : 'DISABLED',
    usingKernelClient: !useFallback,
    quoteUser: q.user,
    effectiveUser,
    addressMatch: effectiveUser.toLowerCase() === String(q.user).toLowerCase(),
  });

  // Approve USDC for escrow (EXACT base units)
  const required = BigInt(q.stake); // e.g. 3000000n for $3 with 6 decimals
  
  // Check balances first
  let balance: bigint = BigInt(0);
  if (publicClient && !useFallback) {
    // Check USDC balance
    balance = await publicClient.readContract({
      address: usdcAddr as Address,
      abi: ERC20_ABI,
      functionName: 'balanceOf',
      args: [effectiveUser as Address],
    }) as bigint;
    defaultLogger.info(`💰 USDC Balance: ${(Number(balance) / 1e6).toFixed(2)} USDC`);
    
    // Check ETH balance (for informational purposes only - not needed with gas sponsorship)
    const ethBalance = await publicClient.getBalance({ address: effectiveUser as Address });
    defaultLogger.info(`⛽ ETH Balance: ${(Number(ethBalance) / 1e18).toFixed(6)} ETH`);
    
    if (balance < required) {
      const shortfall = (Number(required - balance) / 1e6).toFixed(2);
      defaultLogger.error('❌ Insufficient USDC balance', {
        required: (Number(required) / 1e6).toFixed(2),
        balance: (Number(balance) / 1e6).toFixed(2),
        shortfall,
        to: effectiveUser,
      });
      throw new Error(`Insufficient USDC balance. Need ${shortfall} more USDC at ${effectiveUser}`);
    }
  }
  
  if (useFallback && signer) {
    defaultLogger.info('📝 Using fallback signer for approval');
    await ensureAllowanceBaseUnits(signer, usdcAddr, effectiveUser, escrowAddr, required);
  } else if (kernelClient && publicClient) {
    defaultLogger.info('📝 Using Kernel client for approval');
    // Check current allowance using public client (read-only)
    const currentAllowance = await publicClient.readContract({
      address: usdcAddr as Address,
      abi: ERC20_ABI,
      functionName: 'allowance',
      args: [effectiveUser as Address, escrowAddr as Address],
    });
    
    defaultLogger.info(`[allowance] token: ${usdcAddr} owner: ${effectiveUser} spender: ${escrowAddr} current: ${currentAllowance} need: ${required}`);
    
    if (BigInt(currentAllowance) < required) {
      defaultLogger.info('🔄 Approving USDC for escrow (separate UserOperation)');
      
      try {
        defaultLogger.info('📝 Sending approval UserOp to bundler...');
        
        // Encode the approval call data
        const approveCallData = (encodeFunctionData as any)({
          abi: ERC20_ABI_VIEM,
          functionName: 'approve',
          args: [escrowAddr as Address, required],
        });
        
        // Send approval UserOp using sendUserOperation (like the working examples)
        const approveHash = await kernelClient.sendUserOperation({
          callData: await kernelClient.account.encodeCalls([{
            to: usdcAddr as Address,
            value: BigInt(0),
            data: approveCallData,
          }]),
        });
        defaultLogger.info('✅ Approval UserOp sent', { approveHash, jiffyscan: getJiffyscanUrl(approveHash) });
        
        // Wait for approval to be mined (with timeout)
        defaultLogger.info('⏳ Waiting for approval UserOp to be mined...');
        const approveReceipt = await kernelClient.waitForUserOperationReceipt({ 
          hash: approveHash,
          timeout: 60000 // 60 second timeout
        });
        defaultLogger.info('✅ Approval confirmed', { tx: approveReceipt.receipt.transactionHash });
      } catch (error: any) {
        defaultLogger.error('❌ Approval UserOp failed or timed out', {
          name: error?.name,
          message: error?.message,
          code: error?.code,
          data: error?.data,
          cause: error?.cause,
          shortMessage: error?.shortMessage,
          hint: 'Check paymaster funding/policies and bundler status',
        });
        throw new Error(`Failed to approve USDC: ${error?.shortMessage || error?.message || 'Unknown error'}`);
      }
    } else {
      defaultLogger.info('✅ Allowance already sufficient, skipping approval');
    }
  }

  // Optional: log balances for clarity
  if (useFallback && signer) {
  try {
    const token = new Contract(usdcAddr, ERC20_ABI, signer);
      const bal: bigint = await token.balanceOf(effectiveUser);
      defaultLogger.debug('[balance]', { owner: effectiveUser, token: usdcAddr, balance: String(bal) });
  } catch {}
  }

  defaultLogger.info("🎯 Placing Parlay", {
    escrow: escrowAddr,
    stake: q.stake,
    gasSponsored: isGasSponsorshipAvailable() ? 'YES' : 'NO',
    usingKernelClient: !useFallback,
    quoteUser: q.user,
    effectiveUser: effectiveUser,
    smartAccountAddress: smartAccountAddress || 'N/A'
  });

  defaultLogger.debug('📝 Quote Struct for Contract', quoteStruct);
  defaultLogger.debug('✍️ House Signature', { sigHex });
  
  let txHash: string;
  let ticketId: bigint | undefined;
  
  if (useFallback && signer) {
    defaultLogger.info('🚀 Calling escrow.placeParlay via fallback signer...');
    const escrow = new Contract(escrowAddr, ESCROW_ABI, signer);
  const tx = await escrow.placeParlay(quoteStruct, sigHex);
    defaultLogger.info('✅ Transaction sent', { hash: tx.hash });
    
  const rc = await tx.wait();
    defaultLogger.info('✅ Transaction confirmed', { hash: rc.hash });
    txHash = rc.hash;

  try {
    const evt = rc?.logs
      .map((l: any) => { try { return escrow.interface.parseLog(l); } catch { return null; } })
      .find((e: any) => e && e.name === "ParlayPlaced");
    if (evt) ticketId = evt.args?.ticketId as bigint;
  } catch {}
  } else if (kernelClient) {
    defaultLogger.info('🚀 Calling escrow.placeParlay via Kernel client (UserOperation)...');
    
    // Encode the placeParlay call data
    const parlayCallData = (encodeFunctionData as any)({
      abi: ESCROW_ABI_VIEM,
      functionName: 'placeParlay',
      args: [quoteStruct, sigHex],
    });
    
    // Send UserOp using sendUserOperation (like the working examples)
    const userOpHash = await kernelClient.sendUserOperation({
      callData: await kernelClient.account.encodeCalls([{
        to: escrowAddr as Address,
        value: BigInt(0),
        data: parlayCallData,
      }]),
    });
    
    defaultLogger.info('✅ UserOperation sent', { userOpHash, jiffyscan: getJiffyscanUrl(userOpHash) });
    
    // Wait for UserOp to be mined (with timeout)
    defaultLogger.info('⏳ Waiting for UserOp to be mined (this may take 10-30 seconds)...');
    let userOpReceipt;
    try {
      userOpReceipt = await kernelClient.waitForUserOperationReceipt({ 
        hash: userOpHash,
        timeout: 120000 // 120 second timeout
      });
      txHash = userOpReceipt.receipt.transactionHash;
      defaultLogger.info('✅ UserOperation confirmed', { txHash });
      
      // Parse logs to get ticket ID
      try {
        const { Interface } = await import('ethers');
        const iface = new Interface(ESCROW_ABI);
        for (const log of userOpReceipt.receipt.logs) {
          try {
            const parsed = iface.parseLog({ topics: log.topics as string[], data: log.data });
            if (parsed && parsed.name === 'ParlayPlaced') {
              ticketId = parsed.args?.ticketId as bigint;
              defaultLogger.info('🎫 Ticket ID', { ticketId: String(ticketId) });
              break;
            }
          } catch {}
        }
      } catch (error) {
        defaultLogger.warn('Failed to parse ticket ID from logs', error);
      }
    } catch (error) {
      defaultLogger.error('❌ UserOperation failed or timed out', { error, userOpHash, jiffyscan: getJiffyscanUrl(userOpHash) });
      throw error;
    }
  } else {
    throw new Error('No client available for transaction');
  }

  return { 
    txHash, 
    ticketId,
    gasSponsored: !useFallback && isGasSponsorshipAvailable(),
    smartAccountAddress: smartAccountAddress
  };
}

/**
 * Check gas sponsorship status and smart account info
 */
export async function getGasSponsorshipInfo() {
  const isAvailable = isGasSponsorshipAvailable();
  let smartAccountAddress: Address | null = null;
  
  if (isAvailable) {
    try {
      smartAccountAddress = await getSmartAccountAddress();
    } catch (error) {
      console.error('Failed to get smart account address:', error);
    }
  }
  
  return {
    isAvailable,
    smartAccountAddress,
    message: isAvailable 
      ? 'Gas sponsorship is enabled! Your transactions will be sponsored.' 
      : 'Gas sponsorship is not available. You will need to pay gas fees.'
  };
}